import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { requireLiveSession } from "@/lib/requireLiveSession";

/**
 * Whether the signed-in user is the platform founder (a row in public.super_admins).
 * Backed by the SECURITY DEFINER is_super_admin() RPC. This is orthogonal to the
 * admin/agent role in useUserRole — the founder may be an agent-role account.
 *
 * Convenience/UX only: it gates nav visibility and route rendering, but the real
 * enforcement is the founder-invite edge function re-checking membership server-side.
 */
export const useIsSuperAdmin = () => {
  const { user, isLoading: authLoading } = useAuth();

  const { data: isSuperAdmin = false, isLoading } = useQuery({
    queryKey: ["is-super-admin", user?.id],
    queryFn: async (): Promise<boolean> => {
      // Outside the try: a skipped call must not be cached as "not founder" (#794).
      await requireLiveSession();
      try {
        // is_super_admin(_user_id uuid default auth.uid()) — call with no args so
        // it resolves the caller from the JWT.
        const { data, error } = await supabase.rpc("is_super_admin");
        if (error) {
          console.error("Error checking super-admin:", error);
          return false;
        }
        return data === true;
      } catch (err) {
        console.error("Error checking super-admin:", err);
        return false;
      }
    },
    enabled: !authLoading && !!user,
    // Effectively static within a session; refetches naturally on a different user.
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  return {
    isSuperAdmin,
    isLoading: authLoading || isLoading,
  };
};
